import { FC, useCallback } from 'react';
import { Box, Container, Flex, Text } from '@qga/roo-ui/components';
import Icon from 'components/Icon';
import { HiddenOnSmallScreen, ShowOnSmallScreen } from 'components/Layout';
import { BackLink, CloseButton, PageTitle, UppercaseText } from './primitives';
import useQffUser from 'hooks/useQffUser';
import { useParams } from 'react-router';
import { RELATIONSHIP_BASE_URL } from 'config/env';

export interface PageHeaderProps extends Omit<InnerPageHeaderProps, 'tier' | 'onClickCloseSession'> {
  showCloseSessionButton?: boolean;
}

const PageHeader: FC<PageHeaderProps> = ({ showCloseSessionButton = false, ...props }) => {
  const { qffUser } = useQffUser();
  const tier = qffUser?.membershipTier || '';
  const { bookingId } = useParams<{ bookingId?: string }>();

  const closeSession = useCallback(() => {
    window.location.href = `${RELATIONSHIP_BASE_URL}/logout${bookingId ? `?bookingId=${bookingId}` : ''}`;
  }, [bookingId]);

  const onClickCloseSession = showCloseSessionButton ? closeSession : undefined;

  return <InnerPageHeader tier={tier} onClickCloseSession={onClickCloseSession} {...props} />;
};

interface InnerPageHeaderProps {
  title: string;
  tier: string;
  bookingRef?: string;
  backLinkUrl?: string;
  backLinkTitle?: string;
  onClickBackLink?: Function;
  onClickCloseSession?: () => any;
}

const InnerPageHeader: FC<InnerPageHeaderProps> = ({
  title,
  tier,
  bookingRef,
  backLinkUrl,
  backLinkTitle,
  onClickBackLink,
  onClickCloseSession,
}) => (
  <Flex flexDirection="column" borderTop={[1, 1, 0]} borderColor={['greys.alto', 'greys.alto', 'greys.alto']}>
    <Flex minHeight={['60px', '150px']} bg="brand.primary" alignItems="center">
      <Container>
        <Flex justifyContent="space-between" alignItems={['baseline', 'normal']}>
          <Box>
            {backLinkTitle && (
              <BackLink mb={[0, 3]} fontSize="lg" to={backLinkUrl} title={backLinkTitle} onClick={onClickBackLink}>
                <Icon name="keyboardBackspace" mr={2} size={22} /> {backLinkTitle}
              </BackLink>
            )}

            <PageTitle hideOnMobile={!!backLinkTitle}>{title}</PageTitle>
          </Box>
          <Flex flexDirection="column" justifyContent="space-between" alignItems="flex-end">
            <Box>
              {onClickCloseSession && (
                <CloseButton onClick={onClickCloseSession}>
                  Close session <Icon name="close" ml={2} />
                </CloseButton>
              )}
            </Box>
            {bookingRef && (
              <HiddenOnSmallScreen>
                <Text color="white" textAlign="right" data-testid="desktop-booking-ref">
                  Booking reference: {bookingRef}
                </Text>
              </HiddenOnSmallScreen>
            )}
          </Flex>
        </Flex>
      </Container>
    </Flex>

    {bookingRef && (
      <ShowOnSmallScreen>
        <Flex bg="brand.tertiary">
          <Container>
            <Text color="white" data-testid="mobile-booking-ref">
              Booking reference: {bookingRef}
            </Text>
          </Container>
        </Flex>
      </ShowOnSmallScreen>
    )}

    <HiddenOnSmallScreen>
      <Flex bg="brand.tertiary">
        <Container>
          <Flex minHeight="36px" alignItems="center" justifyContent="flex-end">
            <UppercaseText color="white" fontSize="lg" letterSpacing="medium">
              {tier}
            </UppercaseText>
          </Flex>
        </Container>
      </Flex>
    </HiddenOnSmallScreen>
  </Flex>
);

export default PageHeader;
